/* NEM PDP lane: product page behaviour (sizes, notify me, size guide drawer, gallery dots, quick add). */
(() => {
  const SIZE_ALIASES = {
    'x-small': 'xs', 'xs': 'xs', 'small': 's', 's': 's', 'medium': 'm', 'm': 'm', 'large': 'l', 'l': 'l',
    'x-large': 'xl', 'xl': 'xl', 'xx-large': 'xxl', 'xxl': 'xxl', '2xl': 'xxl', 'xxx-large': '3xl', 'xxxl': '3xl', '3xl': '3xl', '4xl': '4xl'
  };
  const norm = (t) => {
    const v = String(t || '').trim().toLowerCase().replace(/\s+/g, ' ');
    return SIZE_ALIASES[v] || v;
  };

  class NemPdp extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.sizeInputs = Array.from(this.querySelectorAll('.nem-size__input'));
      this.variantInput = this.querySelector('[data-variant-input]');
      this.priceEl = this.querySelector('[data-pdp-price]');
      this.addWrap = this.querySelector('[data-add-wrap]');
      this.addBtn = this.querySelector('[data-add-btn]');
      this.addLabel = this.querySelector('[data-add-label]');
      this.notifyOpen = this.querySelector('[data-notify-open]');
      this.notifyPanel = this.querySelector('[data-notify-panel]');
      this.notifyForm = this.notifyPanel ? this.notifyPanel.querySelector('form') : null;
      this.sizeLabel = this.querySelector('[data-size-label]');
      this.sizeStatus = this.querySelector('[data-size-status]');
      this.allSold = this.classList.contains('is-sold-out');
      this.single = this.classList.contains('is-single');
      this.guide = this.querySelector('[data-guide]');

      this.sizeInputs.forEach((i) => i.addEventListener('change', () => this.onSize(i)));
      const checked = this.sizeInputs.find((i) => i.checked);
      if (checked) this.onSize(checked, true);

      if (this.notifyOpen) this.notifyOpen.addEventListener('click', () => this.toggleNotify());
      if (this.notifyForm) this.notifyForm.addEventListener('submit', (e) => this.submitNotify(e));
      this.restorePosted();
      if (window.matchMedia('(max-width: 749px)').matches) this.querySelectorAll('.nem-acc__item[open]').forEach((d) => d.removeAttribute('open'));
      this.initSwatches();
      this.initGallery();
      this.initGuide();
      this.openNotifyFromHash();
    }

    openNotifyFromHash() {
      if (location.hash !== '#nem-notify' || !this.notifyPanel) return;
      const soldOut = this.sizeInputs.find((i) => i.dataset.available !== 'true');
      if (soldOut && !soldOut.checked) { soldOut.checked = true; this.onSize(soldOut); }
      if (this.notifyPanel.hidden && this.notifyOpen) this.toggleNotify();
      const target = this.notifyPanel.hidden ? this : this.notifyPanel;
      requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    }

    onSize(input, initial = false) {
      const available = input.dataset.available === 'true';
      const size = input.dataset.size;
      if (this.variantInput) this.variantInput.value = input.value;
      if (this.sizeLabel) this.sizeLabel.textContent = size;
      if (this.priceEl && input.dataset.price) this.priceEl.textContent = input.dataset.price;
      if (this.sizeStatus) {
        this.sizeStatus.classList.toggle('is-low', available);
        this.sizeStatus.textContent = available
          ? (input.dataset.low === 'true' ? 'Only a few left in this size' : '')
          : 'Size ' + size + ' is sold out. We can let you know when it is back.';
      }
      if (this.addBtn) {
        if (available) {
          this.addBtn.removeAttribute('aria-disabled');
          this.addLabel.textContent = 'Add to Cart';
        } else {
          this.addBtn.setAttribute('aria-disabled', 'true');
          this.addLabel.textContent = 'Sold out';
        }
      }
      if (!this.allSold && this.addWrap) this.addWrap.hidden = !available;
      if (this.notifyOpen) {
        const panelOpen = this.notifyPanel && !this.notifyPanel.hidden;
        this.notifyOpen.hidden = available || panelOpen;
        if (available && this.notifyPanel) {
          this.notifyPanel.hidden = true;
          this.notifyOpen.setAttribute('aria-expanded', 'false');
        }
      }
      this.fillNotify(input);
      if (!initial) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', input.value);
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* ignore */ }
      }
      this.highlightGuide(size);
    }

    fillNotify(input) {
      if (!this.notifyForm) return;
      const f = this.notifyForm;
      const size = input ? input.dataset.size : '';
      const set = (sel, v) => { const el = f.querySelector(sel); if (el) el.value = v; };
      if (input) {
        set('[data-notify-size]', size);
        set('[data-notify-sku]', input.dataset.sku || '');
        set('[data-notify-url]', window.location.origin + this.dataset.productUrl + '?variant=' + input.value);
        const t = f.querySelector('[data-notify-size-text]');
        if (t) t.textContent = ', size ' + size;
      }
    }

    toggleNotify(force) {
      if (!this.notifyPanel) return;
      const open = typeof force === 'boolean' ? force : this.notifyPanel.hidden;
      this.notifyPanel.hidden = !open;
      if (this.notifyOpen) {
        this.notifyOpen.setAttribute('aria-expanded', String(open));
        this.notifyOpen.hidden = open; // the panel replaces the button
      }
      if (open) {
        const name = this.notifyPanel.querySelector('input[name="contact[name]"]');
        if (name && !this.notifyPanel.querySelector('[data-notify-done]:not([hidden])')) name.focus({ preventScroll: true });
        this.notifyPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    submitNotify(e) {
      const f = this.notifyForm;
      if (!this.single && this.sizeInputs.length && !this.sizeInputs.some((i) => i.checked)) {
        e.preventDefault();
        if (this.sizeStatus) {
          this.sizeStatus.classList.remove('is-low');
          this.sizeStatus.textContent = 'Please choose your size first.';
        }
        this.sizeInputs[0].focus();
        return;
      }
      // Native Shopify contact submission (keeps Shopify's spam protection intact). The page returns with
      // form.posted_successfully and we restore an inline confirmation from sessionStorage.
      const get = (n) => { const el = f.querySelector('[name="' + n + '"]'); return el ? el.value : ''; };
      const order = f.querySelector('input[name="contact[Request to order]"]');
      const item = [get('contact[Product]'), get('contact[Colour]'), get('contact[Size]') && get('contact[Size]') !== 'One size' ? 'size ' + get('contact[Size]') : ''].filter(Boolean).join(', ');
      const msg = f.querySelector('[data-notify-message]');
      if (msg) {
        msg.value = 'Notify me when available: ' + item + (get('contact[SKU]') ? ' (SKU ' + get('contact[SKU]') + ')' : '') + '.' +
          (order && order.checked ? ' The customer also asks the shop to order it for them.' : '') + ' ' + get('contact[URL]');
      }
      const checked = this.sizeInputs.find((i) => i.checked);
      try {
        sessionStorage.setItem('nem-notify', JSON.stringify({ path: location.pathname, name: get('contact[name]'), email: get('contact[email]'), item, variant: checked ? checked.value : '' }));
      } catch (x) { /* storage unavailable */ }
      const btn = f.querySelector('[data-notify-submit]');
      if (btn) btn.setAttribute('aria-busy', 'true');
    }

    restorePosted() {
      const p = this.notifyPanel;
      const posted = p && p.querySelector('[data-notify-posted]');
      if (!posted) return;
      let d = null;
      try { d = JSON.parse(sessionStorage.getItem('nem-notify') || 'null'); sessionStorage.removeItem('nem-notify'); } catch (x) { d = null; }
      if (d && d.path === location.pathname) {
        const inp = this.sizeInputs.find((i) => i.value === d.variant);
        if (inp) { inp.checked = true; this.onSize(inp, true); }
        posted.remove();
        this.showDone(d.name, d.email, d.item);
      } else {
        p.querySelector('[data-notify-body]').hidden = true;
        if (this.notifyOpen) this.notifyOpen.hidden = true;
      }
      p.hidden = false;
      // the layout resets scroll for #contact hashes; bring the confirmation into view after it runs
      setTimeout(() => p.scrollIntoView({ block: 'center' }), 60);
    }

    showDone(name, email, item) {
      const p = this.notifyPanel;
      const body = p.querySelector('[data-notify-body]');
      const done = p.querySelector('[data-notify-done]');
      const first = String(name || '').trim().split(/\s+/)[0];
      p.querySelector('[data-notify-done-name]').textContent = first ? ', ' + first : '';
      p.querySelector('[data-notify-done-item]').textContent = item;
      p.querySelector('[data-notify-done-email]').textContent = email;
      body.hidden = true;
      done.hidden = false;
      done.focus({ preventScroll: true });
      if (this.notifyOpen) this.notifyOpen.hidden = true;
    }

    initSwatches() {
      const label = this.querySelector('[data-swatch-label]');
      if (!label) return;
      const def = label.dataset.default;
      this.querySelectorAll('[data-swatch]').forEach((a) => {
        const show = () => { label.textContent = a.dataset.swatch; };
        const reset = () => { label.textContent = def; };
        a.addEventListener('mouseenter', show);
        a.addEventListener('focus', show);
        a.addEventListener('mouseleave', reset);
        a.addEventListener('blur', reset);
      });
    }

    initGallery() {
      const track = this.querySelector('[data-gallery-track]');
      const dots = Array.from(this.querySelectorAll('[data-dot]'));
      if (!track || !dots.length) return;
      const items = Array.from(track.querySelectorAll('[data-gallery-item]'));
      const setActive = (i) => {
        dots.forEach((d, j) => {
          d.classList.toggle('is-active', i === j);
          if (i === j) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
        });
      };
      let raf = 0;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const w = track.clientWidth || 1;
          setActive(Math.round(track.scrollLeft / w));
        });
      }, { passive: true });
      dots.forEach((d, i) => d.addEventListener('click', () => {
        const item = items[i];
        if (item) track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }));
      // pause off-screen videos
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => entries.forEach((en) => {
          const v = en.target.querySelector('video');
          if (!v) return;
          if (en.isIntersecting) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else v.pause();
        }), { threshold: 0.25 });
        items.forEach((it) => { if (it.querySelector('video')) io.observe(it); });
      }
    }

    initGuide() {
      const d = this.guide;
      if (!d) return;
      document.body.appendChild(d); // escape any transformed ancestors
      this.querySelectorAll('[data-open-guide]').forEach((b) => b.addEventListener('click', () => this.openGuide(b)));
      d.querySelectorAll('[data-close-guide]').forEach((b) => b.addEventListener('click', () => this.closeGuide()));
      d.addEventListener('click', (e) => { if (e.target === d) this.closeGuide(); });
      d.addEventListener('cancel', (e) => { e.preventDefault(); this.closeGuide(); });
      const content = d.querySelector('[data-guide-content]');
      d.querySelectorAll('[data-unit-btn]').forEach((b) => b.addEventListener('click', () => {
        if (!content) return;
        content.dataset.unit = b.dataset.unitBtn;
        d.querySelectorAll('[data-unit-btn]').forEach((x) => {
          const on = x === b;
          x.classList.toggle('is-active', on);
          x.setAttribute('aria-pressed', String(on));
        });
        this.revealHighlight();
      }));
      const checked = this.sizeInputs.find((i) => i.checked);
      if (checked) this.highlightGuide(checked.dataset.size);
    }

    openGuide(opener) {
      const d = this.guide;
      this._opener = opener;
      if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
      document.documentElement.classList.add('nem-lock');
      const close = d.querySelector('[data-close-guide]');
      if (close) close.focus({ preventScroll: true });
      this.revealHighlight();
    }

    revealHighlight() {
      const d = this.guide;
      d.querySelectorAll('.nem-sg-scroll').forEach((sc) => {
        const cell = sc.querySelector('thead .is-hl, tbody .is-hl');
        if (!cell || sc.offsetParent === null) return;
        const head = sc.querySelector('tbody th');
        const left = cell.offsetLeft - (head ? head.offsetWidth : 0) - 8;
        if (cell.offsetLeft + cell.offsetWidth > sc.clientWidth) sc.scrollLeft = Math.max(0, left);
      });
    }

    closeGuide() {
      const d = this.guide;
      if (!d.open) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const finish = () => {
        d.classList.remove('is-closing');
        if (typeof d.close === 'function') d.close(); else d.removeAttribute('open');
        document.documentElement.classList.remove('nem-lock');
        if (this._opener) this._opener.focus({ preventScroll: true });
      };
      if (reduce) return finish();
      d.classList.add('is-closing');
      setTimeout(finish, 240);
    }

    highlightGuide(size) {
      const d = this.guide;
      if (!d) return;
      const key = norm(size);
      let any = false;
      d.querySelectorAll('.nem-sg-table').forEach((t) => {
        t.querySelectorAll('.is-hl').forEach((c) => c.classList.remove('is-hl'));
        const rows = Array.from(t.rows);
        if (t.dataset.sizeRow !== undefined) {
          const r = rows[Number(t.dataset.sizeRow)];
          if (!r) return;
          const idx = Array.from(r.cells).findIndex((c, i) => i > 0 && norm(c.textContent) === key);
          if (idx < 1) return;
          rows.forEach((row) => { if (row.cells.length === r.cells.length && row.cells[idx]) row.cells[idx].classList.add('is-hl'); });
          any = true;
        } else if (t.dataset.sizeCol !== undefined) {
          const ci = Number(t.dataset.sizeCol);
          rows.forEach((row, ri) => {
            if (ri === 0) return;
            if (row.cells[ci] && norm(row.cells[ci].textContent) === key) {
              Array.from(row.cells).forEach((c) => c.classList.add('is-hl'));
              any = true;
            }
          });
        }
      });
      const hint = d.querySelector('[data-guide-hint]');
      if (hint) hint.hidden = !any;
    }
  }
  if (!customElements.get('nem-pdp')) customElements.define('nem-pdp', NemPdp);

  class NemQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.toggle = this.querySelector('[data-qa-toggle]');
      this.sizes = this.querySelector('[data-qa-sizes]');
      this.input = this.querySelector('[data-qa-input]');
      this.form = this.querySelector('form');
      this.submit = this.querySelector('[data-qa-submit]');
      if (this.toggle && this.sizes) {
        this.toggle.addEventListener('click', () => {
          const open = this.sizes.hidden;
          this.sizes.hidden = !open;
          this.toggle.setAttribute('aria-expanded', String(open));
          if (open) { const first = this.sizes.querySelector('button:not([disabled])'); if (first) first.focus(); }
        });
      }
      this.querySelectorAll('[data-qa-variant]').forEach((b) => b.addEventListener('click', () => {
        if (b.disabled) return;
        this.querySelectorAll('.is-adding').forEach((x) => x.classList.remove('is-adding'));
        b.classList.add('is-adding');
        this.input.disabled = false;
        this.input.value = b.dataset.qaVariant;
        if (this.form.requestSubmit) this.form.requestSubmit(this.submit); else this.submit.click();
        setTimeout(() => {
          b.classList.remove('is-adding');
          if (this.sizes) this.sizes.hidden = true;
          if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
        }, 900);
      }));
    }
  }
  if (!customElements.get('nem-quick-add')) customElements.define('nem-quick-add', NemQuickAdd);
})();
